import { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import {
  createVerifiedSetupBaseline,
  type VerifiedSetupBaselineInput,
} from '../../../shared/lib/setupRevision';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';

/**
 * Shared, non-React plumbing for the mapping profile controller hooks.
 * Kept hook-free so every controller slice (list, flow snapshot, restore,
 * load actions, CRUD) can import it without creating hook-call cycles.
 */

export type MappingProfileApplyProgressPhase =
  | 'loadProfile'
  | 'applyProfile'
  | 'refreshProfiles'
  | 'complete'
  | 'error';

export interface MappingProfileApplyProgressState {
  profileKey: string;
  profileName: string;
  settingsType: SettingsType;
  phase: MappingProfileApplyProgressPhase;
  message: string;
  detail?: string;
  error?: string;
  startedAt: number;
  elapsedMs?: number;
}

export function isMappingProfileApplyInFlight(
  progress: MappingProfileApplyProgressState | null
): boolean {
  return (
    progress?.phase === 'loadProfile' ||
    progress?.phase === 'applyProfile' ||
    progress?.phase === 'refreshProfiles'
  );
}

export const SlowProfileOperationMs = 8_000;

/** The setup revision a profile persistence checkpoint currently speaks for. */
export function currentSetupRevisionId(): number {
  return useUIStore.getState().setupRevisionId ?? 0;
}

/**
 * Freezes what a read-back actually proved, so a later Discard reverts to this
 * exact setup instead of to whichever profile is selected by then. A
 * verification that cannot name its profile or its document is evidence of
 * nothing and is deliberately not recorded.
 */
export function recordVerifiedSetupBaseline(input: VerifiedSetupBaselineInput): void {
  const baseline = createVerifiedSetupBaseline(input);
  if (!baseline) {
    warnMappingFlow('verified baseline not recorded', {
      profileKey: input.profileKey,
      revisionId: input.revisionId,
    });
    return;
  }

  useUIStore.getState().recordVerifiedSetup(baseline);
}

export function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function elapsedMs(startedAt: number): number {
  return Math.round(nowMs() - startedAt);
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function debugMappingProfile(message: string, payload?: Record<string, unknown>): void {
  traceMappingFlow(
    message.replace(/^\[MappingProfileController\]\s*/, 'profile controller '),
    payload
  );
}

export function startProfileStallWarning(
  event: string,
  payload: Record<string, unknown>,
  startedAt: number
): ReturnType<typeof globalThis.setTimeout> {
  return globalThis.setTimeout(() => {
    warnMappingFlow(event, {
      ...payload,
      elapsedMs: elapsedMs(startedAt),
      slowThresholdMs: SlowProfileOperationMs,
    });
  }, SlowProfileOperationMs);
}
